import { Button, Dialog, NumberField, SelectField, TextField } from '@scalingworks/react-admin-ui';
import React, { useState } from 'react';
import { Props } from './props';
import { useApiUrl, useCustomMutation, useTranslate } from '@refinedev/core';
import {
  StarTransactionAdminActionType as AdjustType,
  ManualAdjustStarInput,
} from '~/api';
import startCase from 'lodash/startCase';
import { join } from 'lodash';

type Adjustment = {
  type: AdjustType;
  stars: number;
  remarks?: string;
};

/**
 * @description
 * Stars are used to upgrade tiers
 */
export const AdjustLoyaltyStars: React.FC<Props> = (props) => {
  const { open, setOpen, userId, onCompleted, starLabel = 'Stars' } = props;

  // ==================== HOOKS
  const apiUrl = useApiUrl();
  const t = useTranslate();

  // ==================== STATES
  const [adjustment, setAdjustment] = useState<Adjustment>({
    type: AdjustType.Grant,
    stars: 0,
    remarks: undefined,
  });

  // ==================== API
  const { mutate: adjustStar, isLoading: updating } = useCustomMutation();

  const onUpdate = () => {
    const { type: action, stars, remarks } = adjustment;
    adjustStar(
      {
        url: apiUrl,
        method: 'post',
        metaData: {
          fields: ['id'],
          operation: 'manualAdjustStars',
          variables: {
            input: {
              type: 'ManualAdjustStarInput!',
              value: {
                toUserIds: [userId],
                action,
                amount: stars,
                remarks,
              } as ManualAdjustStarInput,
            },
          },
        },
        successNotification: {
          message: `${starLabel} ${t('customer.loyalty.updated')}`,
          type: 'success',
        },
        errorNotification: {
          message: t('customer.loyalty.failedToUpdate'),
          type: 'error',
        },
        values: {},
      },
      {
        onSuccess: () => {
          setOpen(false);
          onCompleted?.();
        },
      }
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content className="fixed top-1/3 lg:w-1/2">
          <Dialog.Title>{join([starLabel, t('customer.loyalty.adjustment')], ' ')}</Dialog.Title>
          <div className="flex flex-col space-y-4 p-4 w-full">
            <SelectField
              label={t('customer.loyalty.adjust.actions', {}, 'Actions')}
              options={Object.keys(AdjustType).map((key) => ({
                label: startCase(key),
                value: AdjustType[key as keyof typeof AdjustType],
              }))}
              value={adjustment.type}
              onValue={(type) => setAdjustment((prev) => ({ ...prev, type }))}
              required
            />

            <NumberField
              label={t('customer.loyalty.adjust.stars')}
              value={`${adjustment.stars.toLocaleString()}`}
              onValue={(amount) => setAdjustment((prev) => ({ ...prev, stars: +amount }))}
              required
              suffix={<span className="text-smoke-500">{starLabel}</span>}
              prefix={
                <span className="text-smoke-500">
                  {adjustment.type === AdjustType.Grant ? '+' : '-'}
                </span>
              }
            />

            <TextField
              type="textarea"
              label={t('customer.loyalty.adjust.reason', {}, 'Reason')}
              value={adjustment.remarks}
              onValue={(remarks) => setAdjustment((prev) => ({ ...prev, remarks }))}
              placeholder={t('customer.loyalty.adjust.reasonPlaceholder')}
            />
          </div>

          <div className="flex flex-row justify-end space-x-4">
            <Button variant="default" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!adjustment.stars || !adjustment.type}
              loading={updating}
              variant="solid"
              onClick={onUpdate}
            >
              {t('common.confirm', {}, 'Confirm')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
